import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, Modal, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPlus, faEdit } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { GlobalContext } from "../../../../contexts/GlobalContext";
import axios from "axios";
import {
  keyUserId,
  keyAccessToken,
} from "../../../../assets/js/SessionStorageNames";
import "../../../../assets/css/components/admin/plugins/OdooStyle.css";

export default function BrandsCRUD() {
  const globalContext = useContext(GlobalContext);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: "", plugin: "" });
  const [availablePlugins, setAvailablePlugins] = useState([]);
  const [filterPlugin, setFilterPlugin] = useState("");
  const [filterActive, setFilterActive] = useState("true");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageItemCount = 20;
  const [searchDebounce, setSearchDebounce] = useState(null);

  useEffect(() => {
    fetchAvailablePlugins();
  }, [globalContext.currentEnterpriseId]);

  useEffect(() => {
    fetchBrands();
  }, [filterPlugin, filterActive, currentPage, searchTerm]);

  /**
   * Fetch available plugins
   */
  const fetchAvailablePlugins = () => {
    const entId = globalContext.currentEnterpriseId;
    if (!entId) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/v1/main-plugin/available/${entId}`, {
        headers: {
          "x-api-key": process.env.REACT_APP_API_KEY,
          "x-access-token": localStorage.getItem(keyAccessToken),
          "x-user-id": localStorage.getItem(keyUserId),
        },
      })
      .then((res) => {
        setAvailablePlugins(res.data.data || []);
      })
      .catch((err) => {
        console.error("Error fetching plugins:", err);
      });
  };

  /**
   * Fetch brands from API
   */
  const fetchBrands = () => {
    setLoading(true);
    const params = {
      pageNumber: currentPage,
      pageItemCount: pageItemCount,
    };
    if (filterPlugin) {
      params.plugin = filterPlugin;
    }
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    if (filterActive !== "all") {
      params.isActive = filterActive;
    }

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/v1/brand`, {
        params,
        headers: {
          "x-api-key": process.env.REACT_APP_API_KEY,
          "x-access-token": localStorage.getItem(keyAccessToken),
          "x-user-id": localStorage.getItem(keyUserId),
        },
      })
      .then((res) => {
        const data = res.data.data;
        setBrands(data.brands || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching brands:", err);
        handleAxiosError(err, "Error al obtener las marcas");
        setLoading(false);
      });
  };

  /**
   * Handle axios errors
   */
  const handleAxiosError = (err, msg = null) => {
    if (axios.isCancel(err)) return;

    if (err.response && err.response.data) {
      if (err.response.data.message) {
        globalContext.showModalWithMsg(err.response.data.message);
      } else {
        globalContext.showModalWithMsg(err.response.data);
      }
    } else if (msg) {
      globalContext.showModalWithMsg(msg);
    }
  };

  /**
   * Open modal for creating new brand
   */
  const handleCreate = () => {
    setEditingBrand(null);
    setFormData({ name: "", plugin: "" });
    setShowModal(true);
  };

  /**
   * Open modal for editing brand
   */
  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || "",
      plugin: brand.plugin || "",
    });
    setShowModal(true);
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActive = (brandId, currentStatus) => {
    const newStatus = currentStatus === undefined ? true : !currentStatus;

    axios
      .patch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/brand/${brandId}/toggle`,
        { isActive: newStatus },
        {
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY,
            "x-access-token": localStorage.getItem(keyAccessToken),
            "x-user-id": localStorage.getItem(keyUserId),
          },
        }
      )
      .then((res) => {
        fetchBrands();
      })
      .catch((err) => {
        handleAxiosError(err, "Error al cambiar el estado de la marca");
      });
  };

  /**
   * Handle form submit
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.plugin) {
      globalContext.showModalWithMsg("Por favor complete todos los campos");
      return;
    }

    const url = editingBrand
      ? `${process.env.REACT_APP_BACKEND_URL}/v1/brand/${editingBrand._id}`
      : `${process.env.REACT_APP_BACKEND_URL}/v1/brand`;

    const method = editingBrand ? "put" : "post";

    axios({
      method,
      url,
      data: {
        name: formData.name.trim(),
        plugin: formData.plugin,
      },
      headers: {
        "x-api-key": process.env.REACT_APP_API_KEY,
        "x-access-token": localStorage.getItem(keyAccessToken),
        "x-user-id": localStorage.getItem(keyUserId),
      },
    })
      .then((res) => {
        globalContext.showModalWithMsg(
          editingBrand
            ? "Marca actualizada exitosamente"
            : "Marca creada exitosamente"
        );
        setShowModal(false);
        fetchBrands();
      })
      .catch((err) => {
        handleAxiosError(
          err,
          editingBrand
            ? "Error al actualizar la marca"
            : "Error al crear la marca"
        );
      });
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (e) => {
    setFilterPlugin(e.target.value);
    setCurrentPage(1);
  };

  /**
   * Handle active filter change
   */
  const handleActiveFilterChange = (e) => {
    setFilterActive(e.target.value);
    setCurrentPage(1);
  };

  /**
   * Handle search change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    // Clear previous debounce timeout
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    // Set new debounce timeout (300ms delay)
    const timeout = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 300);

    setSearchDebounce(timeout);
  };

  return (
    <div className="odooFormBody" style={{ padding: "20px" }}>
      <div className="goBack d-flex justify-content-between align-items-center mt-2 mb-4">
        <Link to="/admin/testing/admin">
          <button className="btn-back">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        </Link>
        <h3>Administración de Marcas</h3>
        <Button variant="primary" onClick={handleCreate}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Nueva Marca
        </Button>
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Filtrar por Plugin</Form.Label>
            <Form.Control as="select" value={filterPlugin} onChange={handleFilterChange}>
              <option value="">Todos los plugins</option>
              {availablePlugins.map((plugin) => (
                <option key={plugin.id} value={plugin.id}>
                  {plugin.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Estado</Form.Label>
            <Form.Control as="select" value={filterActive} onChange={handleActiveFilterChange}>
              <option value="true">Activas</option>
              <option value="false">Inactivas</option>
              <option value="undefined">Sin estado</option>
              <option value="all">Todas</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Buscar por Nombre</Form.Label>
            <Form.Control
              type="text"
              placeholder="Escriba para buscar..."
              value={searchInput}
              onChange={handleSearchChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Plugin</th>
                    <th>Brand ID</th>
                    <th>Global Identifier</th>
                    <th className="text-center">Activo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        {searchTerm ? "No se encontraron marcas con ese nombre" : "No hay marcas registradas"}
                      </td>
                    </tr>
                  ) : (
                    brands.map((brand) => (
                      <tr key={brand._id}>
                        <td>{brand.name}</td>
                        <td>{brand.plugin}</td>
                        <td>{brand.brandId}</td>
                        <td>{brand.globalIdentifier || "-"}</td>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={brand.isActive !== false}
                            onChange={() => handleToggleActive(brand._id, brand.isActive)}
                          />
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(brand)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Anterior
                  </Button>
                  <span>
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Modal for Create/Edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingBrand ? "Editar Marca" : "Nueva Marca"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la Marca *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese el nombre de la marca"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                maxLength={1024}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plugin *</Form.Label>
              <Form.Control
                as="select"
                value={formData.plugin}
                onChange={(e) =>
                  setFormData({ ...formData, plugin: e.target.value })
                }
                required
                disabled={!!editingBrand}
              >
                <option value="">Seleccione un plugin</option>
                {availablePlugins.map((plugin) => (
                  <option key={plugin.id} value={plugin.id}>
                    {plugin.name}
                  </option>
                ))}
              </Form.Control>
              {editingBrand && (
                <Form.Text className="text-muted">
                  El plugin no puede ser modificado después de crear la marca.
                </Form.Text>
              )}
            </Form.Group>
            <Form.Text className="text-muted">
              * El Brand ID y Global Identifier se generarán automáticamente.
            </Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingBrand ? "Actualizar" : "Crear"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

